/* Pure theme-owned configuration and bill-of-materials engine. */
(function (global) {
  'use strict';
  const clone = value => JSON.parse(JSON.stringify(value));
  const equal = (a, b) => typeof a === 'boolean' || typeof b === 'boolean' ? a === b : String(a ?? '') === String(b ?? '');
  function match(condition, state) {
    if (!condition) return true;
    if (Array.isArray(condition)) return condition.every(c => equal(state[c.name], c.value));
    if (condition.conditions) {
      const tests = condition.conditions.map(c => match(c, state));
      if (condition.logicalOperator === 'OR') return tests.some(Boolean);
      if (condition.logicalOperator === 'AND') return tests.every(Boolean);
      throw new Error('Unsupported logical operator: ' + condition.logicalOperator);
    }
    const actual = state[condition.name];
    switch (condition.operator || '==') {
      case '==': return equal(actual, condition.value);
      case '!=': return !equal(actual, condition.value);
      case 'includes': return Array.isArray(actual) ? actual.includes(condition.value) : String(actual ?? '').includes(String(condition.value));
      default: throw new Error('Unsupported match operator: ' + condition.operator);
    }
  }
  function expand(schema, state) {
    const result = {...state};
    schema.controls.filter(c => c.type === 'checkbox').forEach(c => {
      c.options.forEach(o => { result[c.key + '__' + o.value] = (state[c.key] || []).includes(o.value); });
    });
    return result;
  }
  const visible = (schema, c, state) => match(c.advancedVariantMatch || c.variantMatch, expand(schema, state));
  function defaults(schema) {
    return Object.fromEntries(schema.controls.map(c => [c.key, c.type === 'checkbox'
      ? c.options.filter(o => o.isDefault).map(o => o.value)
      : (c.options.find(o => o.isDefault)?.value || '')]));
  }
  function transition(schema, oldState, key, value, trigger = 'on-change') {
    const state = clone(oldState);
    if (key) state[key] = value;
    const changed = new Set(key ? [key] : []);
    const fired = new Set();
    for (let pass = 0; pass < 30; pass++) {
      const before = JSON.stringify(state);
      for (const rule of schema.rules) {
        if (rule.trigger !== trigger || fired.has(rule.id)) continue;
        if (trigger === 'on-change' && rule.triggerOnChangeOf?.length && !rule.triggerOnChangeOf.some(k => changed.has(k))) continue;
        if (!match(rule.when, expand(schema, state))) continue;
        fired.add(rule.id);
        for (const effect of rule.effects) {
          if (effect.type !== 'setValue') throw new Error('Unsupported effect: ' + effect.type);
          if (!equal(state[effect.controlKey], effect.value)) {
            state[effect.controlKey] = effect.value;
            changed.add(effect.controlKey);
          }
        }
      }
      // Explicit repair absent from the six exported rules: hidden extension
      // must never leave a Rack BOM active after changing to standalone mounting.
      if (state.c_31 !== 'Rack-Mounted') state.c_2 = 'None';
      for (const c of schema.controls) {
        const values = expand(schema, state);
        if (!visible(schema, c, state) && c.onDisabledSelection === 'deselect') state[c.key] = c.type === 'checkbox' ? [] : '';
        if (c.type === 'checkbox') {
          state[c.key] = (state[c.key] || []).filter(v => {
            const o = c.options.find(o => o.value === v);
            return o && match(o.visibleWhen, values) && match(o.enabledWhen, values);
          });
        } else if (visible(schema, c, state)) {
          const o = c.options.find(o => o.value === state[c.key]);
          if (o && (!match(o.visibleWhen, values) || !match(o.enabledWhen, values))) state[c.key] = '';
        }
      }
      if (before === JSON.stringify(state)) return state;
    }
    throw new Error('Configuration rules did not settle.');
  }
  function parts(schema, state) {
    const values = expand(schema, state);
    return schema.parts.filter(p => match(p.advancedVariantMatch || p.variantMatch, values));
  }
  function bom(schema, state, buildQuantity = 1) {
    if (!Number.isSafeInteger(buildQuantity) || buildQuantity < 1) throw new Error('Enter a positive whole-number quantity.');
    const result = new Map();
    for (const p of parts(schema, state)) {
      const id = p.catalogRef.variantId.split('/').pop();
      const line = result.get(id) || {id, quantity: 0, partIds: [], label: p.label, image: p.catalogRef.imageUrl};
      line.quantity += (p.quantity ?? 1) * buildQuantity;
      line.partIds.push(p.id);
      result.set(id, line);
    }
    return [...result.values()];
  }
  function validate(schema, state) {
    const errors = [];
    for (const c of schema.controls) {
      if (c.required && visible(schema, c, state) && !state[c.key]) errors.push('Choose ' + c.label + '.');
    }
    if (parts(schema, state).filter(p => p.id.startsWith('ft_')).length !== 1) errors.push('The build must resolve to exactly one Functional Trainer variant.');
    return errors;
  }
  function stockErrors(lines, catalog, cartQuantities = {}) {
    return lines.flatMap(line => {
      const v = catalog[line.id];
      if (!v) return ['Missing product data for ' + line.label + ' (' + line.id + ').'];
      if (!v.available) return [line.label + ' is sold out.'];
      const required = line.quantity + (cartQuantities[line.id] || 0);
      if (v.inventory_management && v.inventory_policy !== 'continue' && Number.isFinite(v.inventory_quantity) && required > v.inventory_quantity) return ['Not enough stock for ' + line.label + '.'];
      if (v.minimum && line.quantity < v.minimum) return [line.label + ' requires at least ' + v.minimum + '.'];
      if (v.increment && line.quantity % v.increment) return [line.label + ' requires multiples of ' + v.increment + '.'];
      if (v.maximum && required > v.maximum) return ['Quantity limit exceeded for ' + line.label + '.'];
      return [];
    });
  }
  function total(lines, catalog) {
    return lines.reduce((sum, line) => {
      if (!catalog[line.id] || !Number.isSafeInteger(catalog[line.id].price)) throw new Error('Missing current price for ' + line.id);
      return sum + catalog[line.id].price * line.quantity;
    }, 0);
  }
  const api = {match, expand, visible, defaults, transition, parts, bom, validate, stockErrors, total};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else global.FTNativeEngine = api;
})(typeof window !== 'undefined' ? window : globalThis);
